<template>
  <div ref="container">
    <VirtualCardForm :id :disabled :virtual-form @submit="handleSubmit">
      <template #number>
        <ProcessOutInput
          :disabled
          :label="i18n(translations.numberLabel)"
          v-bind="virtualForm['number']"
          required
        >
          <template #icon>
            <PaymentNetworkRadioGroup
              v-model="selectedNetwork"
              :disabled
              :networks="availableNetworks"
              :preferred-networks
            />
          </template>
        </ProcessOutInput>
      </template>

      <template #expiryDate>
        <ProcessOutInput
          :disabled
          :icon="IconCalendar"
          :label="i18n(translations.dateLabel)"
          v-bind="virtualForm['expiryDate']"
          required
        />
      </template>

      <template #securityCode>
        <ProcessOutInput
          :disabled
          :icon="IconLockLocked"
          input-id="cc-cvc"
          :label="i18n(translations.securityCodeLabel)"
          v-bind="virtualForm['securityCode']"
        />
      </template>
    </VirtualCardForm>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue'

import type { PaymentNetwork } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { IconCalendar } from '@ds/icons/IconCalendar'
import { IconLockLocked } from '@ds/icons/IconLockLocked'

import PaymentNetworkRadioGroup from '../../components/PaymentNetworkRadioGroup/PaymentNetworkRadioGroup.vue'
import VirtualCardForm from '../../components/VirtualCardForm.vue'
import { getRedirectionToPaymentResult } from '../../helpers/getRedirection'
import { PaymentError } from '../../types/PaymentError'
import type {
  PaymentFormAdapterEmits,
  PaymentFormAdapterProps,
  SubmitStartData,
} from '../../types/PaymentFormAdapter'
import { PaymentMethodMisconfiguredError } from '../../types/PaymentMethodMisconfiguredError'
import { PaymentSetupError } from '../../types/PaymentSetupError'
import { PaymentSubmitError } from '../../types/PaymentSubmitError'
import type { VirtualCardConcreteValue } from '../../types/VirtualCardFormState'

import translations from './ProcessOutCardAdapter.translations'
import ProcessOutInput from './ProcessOutInput.vue'
import { useProcessOutForm } from './composables/useProcessOutForm'
import { getExceptionReadableMessage } from './helpers/getExceptionReadableMessage'
import { isProcessOutException } from './helpers/isProcessOutException'
import { normalizeCardType } from './helpers/normalizeCardType'

const props = defineProps<PaymentFormAdapterProps>()
const emit = defineEmits<PaymentFormAdapterEmits>()

const i18n = useI18n()
const $httpFetch = useHttpFetch()

const publicKey = computed(() => {
  const config = props.paymentMethod?.config ?? {}

  return 'publicKey' in config ? config.publicKey : null
})

const preferredNetworks = computed(() => {
  const config = props.paymentMethod?.config ?? {}

  return ('preferredNetworks' in config ? config.preferredNetworks : []) ?? []
})

const {
  setupForm,
  virtualForm,
  tokenizeForm,
  makeCardPayment,
  availableNetworks,
} = useProcessOutForm()
const container = useTemplateRef<HTMLElement>('container')
const selectedNetwork = ref<PaymentNetwork | null>(null)

onMounted(async () => {
  if (container.value === null) {
    throw new Error('PO container not found')
  }

  emit('setup-start')

  try {
    if (publicKey.value === null) {
      throw new PaymentMethodMisconfiguredError('PO public key not found')
    }

    await setupForm(publicKey.value, container.value)

    emit('setup-success')
  } catch (error) {
    const setupError = PaymentSetupError.fromAnyError(error)

    if (isProcessOutException(error)) {
      setupError.readableMessage = getExceptionReadableMessage(i18n, error)
      setupError.source = 'FRONT_LIBRARY_PROCESSOUT'
    }

    emit('setup-error', setupError)
  }
})

const handleSubmit = async (values: VirtualCardConcreteValue) => {
  emit('submit-start', {
    network: selectedNetwork.value,
    availableNetworks: availableNetworks.value,
    preferredNetworks: preferredNetworks.value,
  } as SubmitStartData)

  let originalProcessOutSdkError: unknown | undefined
  try {
    const { token, card } = await tokenizeForm({
      name: values.name,
      selectedNetwork: selectedNetwork.value,
    })

    const {
      paymentId,
      gatewayReference: invoiceId,
      gatewayTokenReference,
    } = await props.createPayment({
      gateway_token_reference: token,
      card: { type: normalizeCardType(card?.type ?? '') },
    })

    if (!invoiceId) {
      throw new PaymentError(
        'No gatewayReference (invoiceId) found after creating the payment',
        {
          type: '/errors/payment/unexpected/api-response',
        },
      )
    }

    try {
      await makeCardPayment(invoiceId, gatewayTokenReference ?? token, {
        authorize_only: !props.paymentMethod?.autoCaptureEnabled,
      })
    } catch (error) {
      // When `makeCardPayment`fails, we expect another potential error
      // from the paymentConfirmCard endpoint which runs in the finally block.
      // Since an error from finally would overwrite the original error,
      // we save the first error now to preserve it for submit-error emission.
      // so we get the full context of the error in the logs.
      originalProcessOutSdkError = error

      throw error
    } finally {
      // calls `paymentConfirmCard` endpoint regardless the payment it is a success or not.
      await $httpFetch('/payment/confirm/card/:paymentId', {
        method: 'POST',
        pathParams: { paymentId },
      })
    }

    emit('submit-success', {
      paymentId,
      redirection: getRedirectionToPaymentResult(paymentId),
    })
  } catch (error) {
    const submitError = PaymentSubmitError.fromAnyError(error)

    if (originalProcessOutSdkError && originalProcessOutSdkError !== error) {
      Object.assign(submitError, {
        originalProcessOutSdkError,
      })
    }

    emit('submit-error', submitError)
  }
}
</script>
